import { Model } from './model';
import { TransformStringToDayJS } from './utils';
import dayjs from 'dayjs';
import { Transform } from 'class-transformer';
import type { Invoice } from './invoice';
import type { CreditMemo } from './credit_memo';
import type { AgentMetrics, AgentParsingErrorCode } from './types';
import { toSmallestUnit } from '@stuut/common/src/utils';

export class PaymentAllocation extends Model {
  id: string;

  agent_confidence_reason: string | null;

  agent_confidence_score: number | null;

  agent_error_code: AgentParsingErrorCode | null;

  agent_uncertainties: string[];

  /**
   * Allocation amount in smallest currency unit (cents).
   * Positive for invoice allocations, negative for credit memo allocations.
   */
  amount: number;

  approved: boolean;

  @TransformStringToDayJS()
  approved_at: dayjs.Dayjs | null;

  adjustment_id: string | null;

  credit_memo_id: string;

  credit_memo: CreditMemo;

  invoice_id: string;

  invoice: Invoice;

  payment_id: string;

  posted: boolean;

  @TransformStringToDayJS()
  posted_at: dayjs.Dayjs | null;

  remote_id: string | null;

  remote_transaction_id: string | null;

  remote_transaction_type: string | null;

  strategy: string;

  get agentMetrics(): AgentMetrics {
    return {
      agentConfidenceReason: this.agent_confidence_reason,
      agentConfidenceScore: this.agent_confidence_score,
      agentErrorCode: this.agent_error_code,
      agentUncertainties: this.agent_uncertainties,
    };
  }

  get isInvalid(): boolean {
    if (this.invoice_id && this.invoice) {
      return this.amount > this.invoice.pending_amount_remaining || this.amount <= 0;
    }
    if (this.credit_memo_id && this.credit_memo) {
      // Credit memo allocations must be negative and not exceed remaining balance
      // Note: credit_memo.net_amount is always in major units (dollars) from the server
      const netAmountInCents =
        toSmallestUnit(this.credit_memo.net_amount, this.credit_memo.currency) ?? 0;
      return Math.abs(this.amount) > netAmountInCents || this.amount >= 0;
    }
    return this.amount === 0;
  }

  get allocationDelta(): number {
    if (!this.invoice) return 0;
    return this.amount - this.invoice.pending_amount_remaining;
  }

  get isCredit(): boolean {
    return Boolean(this.credit_memo_id);
  }
}
